<template>
  <div v-if="!product"></div>

  <div v-else class="flex h-full flex-col rounded-lg border border-main bg-surface" data-testid="page-product-details">
    <div class="flex-1 overflow-y-auto">
      <div class="grid grid-cols-8">
        <div class="col-span-3 flex flex-col border-r border-r-subtle">
          <div class="flex h-50 items-center justify-center rounded-lg bg-solid">
            <img v-if="product.imageUrl" :src="product.imageUrl" alt="Product image" class="h-full" />
          </div>
          <div class="flex flex-col gap-3 p-6">
            <h2 class="txt-headline-2xl">{{ product.name }}</h2>
            <h3 v-if="hasPrice" class="txt-headline-l">
              <span v-if="showFromLabel">{{ $t('FestivalShop.products.details.variants.price_from') }}</span>
              {{ displayPrice }}/{{ $t('FestivalShop.products.details.variants.price_item') }}
            </h3>
            <p v-if="product.shortDescription" class="txt-body-m">
              <MarkdownContent :md="product.shortDescription" />
            </p>
            <p v-if="product.deliveryFee != null" class="txt-body-m">
              {{ $t('FestivalShop.products.details.delivery_fee') }} {{ formatPrice(product.deliveryFee, locale) }}
            </p>
            <div class="flex flex-wrap gap-2">
              <ChipBadge v-if="product.productCategory?.expiresAt" color="default" rounded size="m" type="default">
                {{ `${$t('FestivalShop.products.details.order_by')} ${formattedExpiredDate}` }}
              </ChipBadge>
              <ChipBadge v-if="product.productCategory?.cancellationDate" color="default" rounded size="m" type="default">
                {{ `${$t('FestivalShop.products.details.editable_until')} ${formattedCancellationDate}` }}
              </ChipBadge>
            </div>

            <div v-if="product.productAttachments.length" class="mt-6">
              <h3>{{ $t('FestivalShop.products.details.downloads_title') }}</h3>
              <div class="mt-3 flex flex-col gap-3">
                <DownloadCard
                  v-for="f in product.productAttachments.filter((a) => !!a.url?.length)"
                  :key="f.id"
                  :title="f.name"
                  :description="f.description ?? undefined"
                  :url="f.url!"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="col-span-5 flex w-full flex-col gap-4 p-6">
          <div v-if="editMode">
            <button class="btn-outline-neutral-xs" @click="$router.push({ name: 'shopManageOrders' })">
              <ChevronLeftIcon class="lucide-icon-s" /> {{ $t('FestivalShop.products.details.return_to_order') }}
            </button>
          </div>

          <template v-if="configureOptionsVisible">
            <h3 v-if="!onlyOvernightDeliveryCalloutVisible" class="txt-headline-l">
              {{ $t('FestivalShop.products.details.configure_title') }}
            </h3>

            <div v-if="variantSelectionRequired" class="relative">
              <p class="mb-0.5 txt-body-s-bold">{{ $t('FestivalShop.products.details.configure.variant') }}</p>
              <SelectField
                id="variant"
                v-model="variantId"
                name="variant"
                :placeholder="t('FestivalShop.products.selectVariant')"
                :size="'l'"
              >
                <option v-for="(variant, i) in product.variants" :key="variant.id" :value="variant.id">
                  {{ `${variant.name} ${variantPriceDiff(i, variant.price ?? 0)}` }}
                </option>
              </SelectField>
              <ChipBadge
                v-if="selectedVariant && selectedVariant.price != null"
                color="default"
                rounded
                size="s"
                type="default"
                class="absolute top-9 right-9"
              >
                {{ formatPrice(selectedVariant.price, locale) }}</ChipBadge
              >
            </div>

            <div v-if="deliveryTimeRequired && !isOvernightByDefault" class="relative">
              <p class="mb-0.5 txt-body-s-bold">{{ $t('FestivalShop.products.details.configure.delivery_time.title') }}</p>
              <SelectField
                id="deliveryTimeId"
                v-model="deliveryTimeId"
                name="deliveryTime"
                :size="'l'"
                @update:model-value="updateDeliverySelection"
              >
                <option v-if="showRequiredDeliveryTimeslotPlaceholder" :value="''" disabled>
                  {{ requiredDeliveryTimeslotPlaceholder }}
                </option>
                <option v-if="!deliveryTimeslotSelectionRequired" :value="''">
                  {{ $t(defaultDeliveryPhraseKey) }}
                </option>
                <option
                  v-for="slot in deliverySlotOptions"
                  :key="slot.id"
                  :value="slot.id"
                  :disabled="selectedSlotUnavailable && slot.id === product.productCategory?.kioskSelectedDeliverySlot?.id"
                >
                  {{ formatDeliverySlot(slot, locale)
                  }}{{
                    selectedSlotUnavailable && slot.id === product.productCategory?.kioskSelectedDeliverySlot?.id
                      ? ` - ${t('FestivalShop.products.details.configure.delivery_time.selected_unavailable')}`
                      : ''
                  }}
                </option>
              </SelectField>
              <p class="mt-1 txt-label-s text-subtle">
                {{ $t('FestivalShop.products.details.configure.delivery_time.selection_callout') }}
              </p>
            </div>
            <MessageBanner v-if="isOvernightByDefault" type="neutral" :message="$t(defaultDeliveryCalloutKey)" />

            <div v-if="uploadRequired" class="flex flex-col gap-2">
              <p class="mb-0.5 txt-body-s-bold">{{ $t('FestivalShop.products.details.configure.upload_title') }}</p>
              <p class="txt-body-s">
                {{ $t('FestivalShop.products.details.configure.upload_note') }}<br /><br />{{
                  $t('FestivalShop.products.details.configure.upload_file_formats')
                }}
              </p>

              <div v-if="existingFiles.length" class="my-2 flex flex-col gap-2">
                <UploadedFile
                  v-for="f in existingFiles"
                  :id="f.id"
                  :key="f.id"
                  :name="f.filename"
                  :url="f.url"
                  :created-at="f.createdAt"
                  :allow-remove="allowFileRemoval"
                  :allow-replace="allowFileReplace"
                  @remove="handleRemoveFile"
                  @replace="handleReplaceFile"
                />
              </div>

              <FileUploader v-model="files" :accept="['image/png', 'image/svg', '.pdf']" />
            </div>
            <div v-if="product.commentAllowed">
              <p class="mb-0.5 txt-body-s-bold">
                {{ $t('FestivalShop.products.details.configure.comment') }} <span class="txt-body-s text-subtlest">(optional)</span>
              </p>
              <TextareaField id="comment" v-model="comment" placeholder="Any notes for our logistics team?" />
            </div>
          </template>

          <div v-if="additionalInformationVisible">
            <hr v-if="configureOptionsVisible" class="mt-6 mb-8 border border-subtle" />
            <div class="cursor-pointer" @click="showAdditionalInfo = !showAdditionalInfo">
              <h3 class="flex justify-between txt-headline-l">
                <span>{{ $t('FestivalShop.products.details.additional_information.title') }} </span>
                <span
                  ><ChevronDownIcon v-if="!showAdditionalInfo" class="lucide-icon-m" /><ChevronUpIcon v-else class="lucide-icon-m"
                /></span>
              </h3>
            </div>
            <p v-if="product.description && showAdditionalInfo" class="mt-4 txt-body-m">
              <MarkdownContent :md="product.description" />
            </p>
          </div>
        </div>
      </div>
    </div>

    <ProductDetailsFooter
      v-if="categoryEditable"
      class="sticky bottom-0"
      :order-item-state="{
        count,
        price: selectedVariant?.price || 0,
        deliveryFee: product?.deliveryFee ?? undefined,
        maximum: maximumSelectable,
      }"
      :edit-mode="editMode"
      :add-to-order-enabled="addToOrderEnabled"
      :update-order-enabled="updateOrderEnabled"
      :all-variants-free="allVariantsFree"
      @update:quantity="handleQuantityChange"
      @add-to-cart="handleAddToCart"
      @update-order="handleUpdateOrder"
    />
  </div>
</template>

<script setup lang="ts">
import { ChipBadge, MarkdownContent, MessageBanner, SelectField, TextareaField } from '@ramp106/omrjs-core-ui'
import { ChevronDownIcon, ChevronLeftIcon, ChevronUpIcon } from '@ramp106/omrjs-core-ui/icons'
import { storeToRefs } from 'pinia'
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { formatPrice, formatPriceRange, isProductCategoryEditable } from '@/components/FestivalShop/helpers'
import { FestivalShopCartKey, useFestivalShopCart } from '@/composables/useFestivalShopCart'
import {
  ExtrasOrdersDocument,
  ProductByIdDocument,
  useDeleteKioskCategoryDeliverySlotMutation,
  useSetKioskCategoryDeliverySlotMutation,
  useUpdateExtrasOrderMutation,
  type Event,
  type Extra,
  type FestivalShopProductsQuery,
  type ProductCategoryDeliverySlot,
} from '@/gql/myomr'
import { toGlobalId } from '@/helpers/globalIdHelper'
import { showNotification } from '@/helpers/notificationHelper'
import { globalizeUrlifiedId } from '@/helpers/urlifyGlobalId'
import { useLocaleStore } from '@/stores'
import { useFestivalShopStore } from '@/stores/festivalShop'

import { getLockedSnapshotCount } from './orderEditorItem'
import DownloadCard from './Products/DownloadCard.vue'
import FileUploader from './Products/FileUploader.vue'
import ProductDetailsFooter from './Products/ProductDetailsFooter.vue'
import UploadedFile from './Products/UploadedFile.vue'

import { formatDeliverySlot, formatLocalDate } from '.'

const props = defineProps<{
  productId: string
  products: FestivalShopProductsQuery['festivalShopProducts']['nodes']
  productsLoading: boolean
  event: Event
  orderData?: Extra
  orderId?: string
  kioskId: string
}>()

const editMode = ref<boolean>(!!props.orderData)
const showAdditionalInfo = ref<boolean>(true)

const count = ref<number>(props.orderData?.count ?? 1)
const comment = ref<string>(props.orderData?.comment ?? '')
const variantId = ref<string | undefined>(props.orderData?.variant.id)
const files = ref<File[]>([])
const removedFileIds = ref<string[]>([])
const initialOrderState = ref({
  count: props.orderData?.count ?? 1,
  comment: props.orderData?.comment ?? '',
  variantId: props.orderData?.variant?.id,
})

const festivalShopCart = inject(FestivalShopCartKey)
if (!festivalShopCart) {
  throw new Error('FestivalShopCart not provided')
}

const { t } = useI18n()
const { locale } = storeToRefs(useLocaleStore())
const { mutate: updateExtrasOrder } = useUpdateExtrasOrderMutation()

const productId = computed(() => toGlobalId('Product', props.productId))
const product = computed(() => props.products.find((p) => p.id === productId.value))
const extrasOrderId = computed(() => (props.orderId ? toGlobalId('ExtrasOrder', props.orderId) : undefined))
const rootEventSlug = computed(() => props.event.rootEvent?.slug ?? props.event.slug ?? '')
const festivalShopStore = useFestivalShopStore()
const normalizedKioskId = computed(() => {
  return props.kioskId.startsWith('gid://myomr/') ? props.kioskId : globalizeUrlifiedId(props.kioskId)
})

const deliveryTimeId = ref<string>(product.value?.productCategory?.kioskSelectedDeliverySlot?.id ?? '')
const cartVariantId = computed(() => selectedVariant.value?.id ?? 'default-variant-id')

const variantPriceDiff = (index: number, price?: number) => {
  if (index < 1 || (product.value?.variants?.length ?? 0) < 2 || price == null) return ''
  const basePrice = product.value?.variants[0].price ?? 0
  const diff = price - basePrice
  if (diff === 0) return ''
  return ` (${diff > 0 ? '+' : '-'} ${formatPrice(Math.abs(diff), locale.value)})`
}

const additionalInformationVisible = computed(() => !!product.value?.description?.length)

const configureOptionsVisible = computed(
  () => deliveryTimeRequired.value || variantSelectionRequired.value || uploadRequired.value || product.value?.commentAllowed,
)
const onlyOvernightDeliveryCalloutVisible = computed(
  () => isOvernightByDefault.value && !variantSelectionRequired.value && !uploadRequired.value && !product.value?.commentAllowed,
)

watch(
  product,
  (currentProduct) => {
    festivalShopStore.setCurrentProduct(currentProduct ?? null)
  },
  { immediate: true },
)

watch(
  () => product.value?.productCategory?.kioskSelectedDeliverySlot?.id,
  (selectedSlotId) => {
    deliveryTimeId.value = selectedSlotId ?? ''
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  festivalShopStore.setCurrentProduct(null)
})

const maximumSelectable = computed(() => {
  let max = Number.MAX_VALUE
  const reservedCount = editMode.value ? (props.orderData?.count ?? 0) : 0

  if (product.value?.maximumLeft != undefined) {
    let maxLeft = product.value.maximumLeft + reservedCount

    if (!editMode.value && festivalShopCart.isInCart(product.value.id, cartVariantId.value)) {
      const alreadyInCartQuantity = festivalShopCart.getCartItem(product.value.id, cartVariantId.value)?.count ?? 0
      maxLeft = Math.max(0, maxLeft - alreadyInCartQuantity)
    }
    max = Math.min(max, maxLeft)
  } else if (product.value?.maximum != undefined) {
    max = product.value.maximum
  }

  if (product.value?.eventMaximumLeft != undefined) {
    max = Math.min(max, product.value.eventMaximumLeft + reservedCount)
  }

  return max == Number.MAX_VALUE ? undefined : max
})

const variantSelectionRequired = computed(() => (product.value?.variants.length ?? 0) > 1)
const uploadRequired = computed(() => product.value?.fileUploadRequired)
const deliveryTimeRequired = computed(() => product.value?.productCategory?.enableDeliveryTimeslots)
const deliveryTimeslotSelectionRequired = computed(
  () => deliveryTimeRequired.value && !!product.value?.productCategory?.deliveryTimeslotSelectionRequired,
)
const deliverySelectionValid = computed(() =>
  deliveryTimeslotSelectionRequired.value ? !!product.value?.productCategory?.kioskSelectedDeliverySlot?.id : true,
)
const showRequiredDeliveryTimeslotPlaceholder = computed(() => {
  if (!deliveryTimeslotSelectionRequired.value) return false
  if (product.value?.productCategory?.kioskSelectedDeliverySlot?.id) return false
  return !deliveryTimeId.value
})
const selectedSlotUnavailable = computed(() => {
  const selectedSlot = product.value?.productCategory?.kioskSelectedDeliverySlot
  if (!selectedSlot) return false
  return !(product.value?.productCategory?.deliverySlots ?? []).some((slot) => slot.id === selectedSlot.id)
})
const deliverySlotOptions = computed<ProductCategoryDeliverySlot[]>(() => {
  const category = product.value?.productCategory
  if (!category) return []
  if (!selectedSlotUnavailable.value || !category.kioskSelectedDeliverySlot) return category.deliverySlots
  return [category.kioskSelectedDeliverySlot, ...category.deliverySlots]
})
const defaultDeliveryPhraseKey = computed(() =>
  (product.value?.productCategory as { secondDayDeliveryDefault?: boolean } | null | undefined)?.secondDayDeliveryDefault
    ? 'FestivalShop.products.details.configure.delivery_time.second_day_overnight_key'
    : 'FestivalShop.products.details.configure.delivery_time.overnight_key',
)
const defaultDeliveryCalloutKey = computed(() => {
  return product.value?.productCategory?.secondDayDeliveryDefault
    ? 'FestivalShop.products.details.configure.delivery_time.second_day_overnight_callout'
    : 'FestivalShop.products.details.configure.delivery_time.overnight_callout'
})
const isOvernightByDefault = computed(
  () => deliveryTimeRequired.value && !deliveryTimeslotSelectionRequired.value && !deliverySlotOptions.value.length,
)
const requiredDeliveryTimeslotPlaceholder = computed(() =>
  locale.value === 'de' ? 'Bitte wähle eine Lieferzeit' : 'Please select a delivery time',
)

const variantSelectionValid = computed(() => !!variantId.value)
const uploadSelectionValid = computed(() => (uploadRequired.value ? (files.value ?? []).length > 0 : true))
const existingFiles = computed(() => (props.orderData?.files ?? []).filter((file) => !removedFileIds.value.includes(file.id)))
const categoryEditable = computed(() => isProductCategoryEditable(product.value?.productCategory))
const lockedSnapshotCount = computed(() => getLockedSnapshotCount(props.orderData))

const allowFileRemoval = computed(() => categoryEditable.value && !(uploadRequired.value && existingFiles.value.length === 1))
const allowFileReplace = computed(() => categoryEditable.value)

const addToOrderEnabled = computed(() => {
  return (
    categoryEditable.value &&
    (variantSelectionRequired.value ? variantSelectionValid.value : true) &&
    (uploadRequired.value ? uploadSelectionValid.value : true) &&
    deliverySelectionValid.value
  )
})
const quantityValid = computed(() => {
  return maximumSelectable.value != undefined ? count.value <= maximumSelectable.value : true
})

const updateOrderEnabled = computed(() => {
  const currentVariantId = selectedVariant.value?.id ?? variantId.value
  const hasPendingChanges =
    count.value !== initialOrderState.value.count ||
    comment.value !== initialOrderState.value.comment ||
    currentVariantId !== initialOrderState.value.variantId ||
    files.value.length > 0
  return categoryEditable.value && hasPendingChanges && variantSelectionValid.value
})

const selectedVariant = computed(() => {
  // autoselect if there is only 1 variant
  if (product.value?.variants.length === 1) {
    return product.value.variants[0]
  }
  return product.value?.variants.filter((v) => v.id == variantId.value).at(0)
})

const formattedExpiredDate = computed(() => {
  return formatLocalDate(locale.value, product.value?.productCategory?.expiresAt)
})
const formattedCancellationDate = computed(() => {
  return formatLocalDate(locale.value, product.value?.productCategory?.cancellationDate)
})

const variantPrices = computed(() => {
  return (
    product.value?.variants
      .map((v) => v.price)
      .filter((p): p is number => p != null)
      .sort((a, b) => a - b) ?? []
  )
})

const allVariantsFree = computed(() => {
  return variantPrices.value.length > 0 && variantPrices.value.every((price) => price === 0)
})

const showPriceRange = computed(() => {
  if ((product.value?.variants.length ?? 0) <= 1 || variantPrices.value.length < 2) return false
  const minPrice = variantPrices.value[0]
  const maxPrice = variantPrices.value.at(-1)
  return minPrice === 0 && maxPrice != null && maxPrice > minPrice
})

const showFromLabel = computed(() => (product.value?.variants.length ?? 0) > 1 && !showPriceRange.value)

const hasPrice = computed(() => displayPrice.value !== '-NA-')

const displayPrice = computed(() => {
  if (!variantPrices.value.length) return '-NA-'
  if (showPriceRange.value) {
    return formatPriceRange(variantPrices.value[0], variantPrices.value.at(-1) ?? variantPrices.value[0], locale.value)
  }
  return formatPrice(variantPrices.value[0], locale.value)
})

const handleQuantityChange = (newCount: number) => {
  if (newCount <= 0) return
  if (maximumSelectable.value != null) {
    count.value = Math.min(newCount, maximumSelectable.value)
    return
  }
  count.value = newCount
}

const { mutate: setDeliverySlot } = useSetKioskCategoryDeliverySlotMutation()
const { mutate: deleteDeliverySlot } = useDeleteKioskCategoryDeliverySlotMutation()

const clearDeliverySelection = async () => {
  if (!product.value?.productCategory) return false

  try {
    const result = await deleteDeliverySlot(
      { kioskId: normalizedKioskId.value, productCategoryId: product.value.productCategory.id },
      {
        refetchQueries: ['FestivalShopProductCategories', 'FestivalShopProducts'],
      },
    )

    const errors = result?.data?.deleteKioskCategoryDeliverySlot?.errors ?? []
    if (errors.length) {
      showNotification(errors.join(', '), 'error')
      return false
    }

    deliveryTimeId.value = ''
    return true
  } catch {
    showNotification(t('FestivalShop.products.details.notification.delivery_time_change_error'), 'error')
    return false
  }
}

const updateDeliverySelection = async (deliveryId: string) => {
  if (!product.value?.productCategory) return

  if (!deliveryId.length) {
    if (deliveryTimeslotSelectionRequired.value) {
      showNotification(t('FestivalShop.products.details.disabled_not_configured'), 'error')
      return
    }
    await clearDeliverySelection()
    return
  }

  try {
    const result = await setDeliverySlot(
      {
        kioskId: normalizedKioskId.value,
        productCategoryDeliverySlotId: deliveryId,
        productCategoryId: product.value.productCategory.id,
      },
      {
        refetchQueries: ['FestivalShopProductCategories', 'FestivalShopProducts'],
      },
    )

    const errors = result?.data?.createKioskCategoryDeliverySlot?.errors ?? []
    if (errors.length) {
      const isCleared = await clearDeliverySelection()
      if (isCleared) {
        showNotification(t('FestivalShop.products.details.notification.delivery_time_unavailable_reset'), 'warning')
      }
      return
    }

    showNotification(t('FestivalShop.products.details.notification.delivery_time_changed'), 'success')
  } catch {
    showNotification(t('FestivalShop.products.details.notification.delivery_time_change_error'), 'error')
  }
}

const getUpdateExtrasOrderMutationOptions = () => ({
  refetchQueries: [
    {
      query: ProductByIdDocument,
      variables: {
        rootEvent: { slug: rootEventSlug.value },
        kioskId: normalizedKioskId.value,
        id: productId.value,
      },
    },
    {
      query: ExtrasOrdersDocument,
      variables: {
        rootEvent: { slug: rootEventSlug.value },
        kioskFilter: { id: normalizedKioskId.value },
      },
    },
  ],
  awaitRefetchQueries: true,
})

const handleAddToCart = () => {
  if (!product.value) {
    showNotification(t('FestivalShop.products.details.errors.product_not_found'), 'error')
    return
  }
  if (!addToOrderEnabled.value) {
    showNotification(t('FestivalShop.products.details.disabled_not_configured'), 'error')
    return
  }

  const existingCartCount = festivalShopCart.getCartItem(product.value.id, cartVariantId.value)?.count ?? 0
  const maximum = maximumSelectable.value != undefined ? existingCartCount + maximumSelectable.value : undefined

  festivalShopCart.addToCart({
    productId: product.value.id,
    productCategoryId: product.value.productCategory?.id,
    productName: product.value.name,
    variantId: cartVariantId.value,
    variantName: variantSelectionRequired.value ? selectedVariant.value?.name : undefined,
    count: count.value,
    maximum,
    price: selectedVariant.value?.price || 0,
    deliveryFee: product.value.deliveryFee || undefined,
    comment: comment.value?.length ? comment.value : undefined,
    file: files.value,
    imageUrl: product.value.imageUrl || undefined,
  })

  showNotification(`${product.value.name} ${t('FestivalShop.products.details.notification.added_to_cart')}`, 'success', undefined, true, [
    { title: t('FestivalShop.products.details.notification.show_cart'), handler: openShoppingCart },
  ])
  files.value = []
}

const handleUpdateOrder = async () => {
  try {
    if (!props.orderData || !props.orderData.kiosk || !product.value) return
    if (!extrasOrderId.value) return

    if (!categoryEditable.value || !quantityValid.value) return

    const extrasOrderResult = await updateExtrasOrder(
      {
        kioskId: normalizedKioskId.value,
        extras: [
          {
            productId: product.value?.id,
            variantId: selectedVariant.value?.id ?? 'default-variant-id',
            count: count.value + lockedSnapshotCount.value,
            comment: comment.value?.length ? comment.value : undefined,
            files: files.value,
          },
        ],
        rootEvent: { slug: rootEventSlug.value },
        extrasOrderId: extrasOrderId.value,
      },
      getUpdateExtrasOrderMutationOptions(),
    )

    const extrasOrderErrors = extrasOrderResult?.data?.updateExtrasOrder?.errors || []
    if (extrasOrderErrors.length > 0) {
      showNotification(extrasOrderErrors.join(', '), 'error')
      return
    }

    showNotification(t('FestivalShop.products.details.notification.order_updated'), 'success')
    files.value = []
    initialOrderState.value = {
      count: count.value,
      comment: comment.value,
      variantId: selectedVariant.value?.id ?? variantId.value,
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : t('FestivalShop.checkout.generalError')
    showNotification(message, 'error')
  }
}

const { showCart } = useFestivalShopCart()

const openShoppingCart = () => {
  showCart()
}

const handleRemoveFile = async (fileId: string) => {
  try {
    if (!props.orderData || !props.orderData.kiosk || !product.value) return
    if (!extrasOrderId.value) return

    if (!categoryEditable.value) return

    await updateExtrasOrder(
      {
        kioskId: normalizedKioskId.value,
        extras: [
          {
            productId: product.value.id,
            variantId: selectedVariant.value?.id ?? 'default-variant-id',
            count: count.value + lockedSnapshotCount.value,
            comment: comment.value?.length ? comment.value : undefined,
            filesToRemove: [fileId],
          },
        ],
        rootEvent: { slug: rootEventSlug.value },
        extrasOrderId: extrasOrderId.value,
      },
      getUpdateExtrasOrderMutationOptions(),
    )

    removedFileIds.value = [...removedFileIds.value, fileId]
    showNotification(t('FestivalShop.products.details.notification.order_updated'), 'success')
  } catch (error) {
    const message = error instanceof Error ? error.message : t('FestivalShop.checkout.generalError')
    showNotification(message, 'error')
  }
}

const handleReplaceFile = async ({ file, fileId }: { file: File; fileId: string }) => {
  try {
    if (!props.orderData || !props.orderData.kiosk || !product.value) return
    if (!extrasOrderId.value) return

    if (!categoryEditable.value) return

    await updateExtrasOrder(
      {
        kioskId: normalizedKioskId.value,
        extras: [
          {
            productId: product.value.id,
            variantId: selectedVariant.value?.id ?? 'default-variant-id',
            count: count.value + lockedSnapshotCount.value,
            comment: comment.value?.length ? comment.value : undefined,
            files: [file],
            filesToRemove: [fileId],
          },
        ],
        rootEvent: { slug: rootEventSlug.value },
        extrasOrderId: extrasOrderId.value,
      },
      getUpdateExtrasOrderMutationOptions(),
    )

    showNotification(t('FestivalShop.products.details.notification.order_updated'), 'success')
  } catch (error) {
    const message = error instanceof Error ? error.message : t('FestivalShop.checkout.generalError')
    showNotification(message, 'error')
  }
}
</script>
